import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { Form, Button } from "reactstrap";
import cx from "classnames";

import {
  addDocumentsToCollections,
  getAllCollections,
  removeDocumentsFromCollections,
} from "../../../store/actions/documentActions";
import { preventFormSubmitOnEnter as preventSubmit } from "../../../_helpers/utils";
import { Input } from "../../ui/input";
import { Icon } from "../../ui/icon";
import { ProcessLoader } from "../../ui/loader/processLoader";
import { FlashAlert } from "../../ui/flashAlert";

import global from "../../../scss/dhp.scss";

let style = Object.assign({}, global);

const AddToCollectionModal = ({ document, _helpers }) => {
  const dispatch = useDispatch();
  const userId = useSelector(state => state?.auth?.uid);
  const { collectionId } = useParams();
  const isSingleCollection = !!collectionId;
  const alertData = {
    show: false,
    type: "",
  };

  const form = {
    collections: {
      value: [],
      validation: {
        required: true,
        not_equals: "",
      },
      valid: false,
    },
  };

  const [formData, setFormData] = useState(form);
  const [formValid, setFormValid] = useState(false);
  const [alert, setAlert] = useState(alertData);
  const [options, setOptions] = useState([]);
  const [disable, setDisable] = useState(false);
  const [inProgress, setInprogress] = useState(false);
  const [defaultVal, setDefaultVal] = useState([]);
  const [initialSelection, setInitialSelection] = useState([]);

  const toggleAlert = () => setAlert({ ...alert, show: !alert.show });

  const updateForm = (e, field) => {
    let updatedForm = { ...formData };
    updatedForm[field].value = e;
    updatedForm[field].valid = true
    setFormData(updatedForm);
    setFormValid(Object.keys(formData).every(field => formData[field].valid));
  };

  const handleSubmit = e => {
    if (!formValid) return;
    e.preventDefault();
    setInprogress(true);

    const currentIds = formData.collections.value;
    const addedIds = currentIds.filter(id => !initialSelection.includes(id));
    const removedIds = initialSelection.filter(id => !currentIds.includes(id));

    // No changes
    if (addedIds.length === 0 && removedIds.length === 0) {
      setInprogress(false);
      return;
    }

    const apiCalls = [];

    if (addedIds.length) {
      apiCalls.push(
        dispatch(
          addDocumentsToCollections({
            collection_ids: addedIds,
            document_ids: [document.id],
          })
        )
      );
    }

    if (removedIds.length) {
      apiCalls.push(
        dispatch(
          removeDocumentsFromCollections({
            collection_ids: removedIds,
            document_id: document.id,
          })
        )
      );
    }

    Promise.all(apiCalls)
      .then(responses => {
        const success = responses.every(r => r?.data?.success);
        if (success) {
          if (isSingleCollection && _helpers?.gridView?.fetch?.setPayload)
            _helpers.gridView.fetch.setPayload({ ..._helpers.gridView.fetch.payload, page: 1 });
          _helpers.modal.toggle();
        } else {
          setAlert({ ...alert, show: true, type: "error" });
        }
      })
      .catch(() => setAlert({ ...alert, show: true, type: "error" }))
      .finally(() => {
        setInprogress(false);
        setDisable(true);
      });
  };

  useEffect(() => {
    // To manage, if from Create Collection Modal
    const fromCreateNew = sessionStorage.getItem("fromCreateNewModal") === "true";
    sessionStorage.removeItem("fromCreateNewModal");

    dispatch(getAllCollections()).then(resp => {
      const data = resp?.data?.data;

      const formattedOptions = [];

      if (data?.ME?.length > 0) {
        formattedOptions.push(
          ...data.ME.map(item => ({
            id: item._id,
            name: item.name,
            schemeType: "My Collections",
            slug: item.slug,
            color: item.color,
            documents: item.documents,
          }))
        );
      }

      if (data?.SHARED?.length > 0) {
        formattedOptions.push(
          ...data.SHARED.map(item => ({
            id: item._id,
            name: item.name,
            schemeType: "Shared with Me",
            slug: item.slug,
            color: item.color,
            documents: item.documents,
            sharedPermission: item.shared_with?.[userId] || null,
          }))
        );
      }

      setOptions(formattedOptions);

      const existingIds = formattedOptions.filter(opt => opt.documents.includes(document.id)).map(opt => opt.id);

      const selectedIds = [...existingIds];

      if (fromCreateNew && data?.ME?.length > 0) {
        const targetId = data.ME[0]._id;
        if (!selectedIds.includes(targetId)) selectedIds.unshift(targetId);
      }

      setDefaultVal(selectedIds);
      setInitialSelection(existingIds);

      setFormData(prev => ({
        ...prev,
        collections: {
          ...prev.collections,
          value: selectedIds,
        },
      }));
    });
  }, []);

  return (
    <React.Fragment>
      <h4 className={cx(style["fw-7"], style["mb-4"])}> Add to Collection</h4>
      <Link to="#" className={cx(style["cross-modal"], style["rounded"])} onClick={_helpers?.modal?.toggle}>
        <Icon icon="ui-close" />
      </Link>

      <Form className={style["mt-1"]} onKeyDown={e => preventSubmit(e)} onSubmit={e => handleSubmit(e)}>
        <Input
          cssModule={style}
          returnType="formGroup"
          formGroupClass={style["w-100"]}
          type="tabular_multi_select"
          placeholder=" Choose Collection"
          noOptionMessage="No collections found"
          options={options}
          defaultValue={defaultVal}
          optionValue={"id"}
          optionTextPrimary={["name"]}
          updateState={e => updateForm(e, "collections")}
          schemeTypeArray={["My Collections", "Shared with Me"]}
          _helpers={_helpers}
        />

        <div className={cx(style["align-items-center"], style["d-flex"])}>
          <Button
            type="submit"
            className={style["mt-2"]}
            color="primary"
            cssModule={style}
            disabled={
              !formValid ||
              disable ||
              inProgress ||
              (formData.collections.value.length === initialSelection.length &&
                formData.collections.value.every(id => initialSelection.includes(id)))
            }>
            Save
          </Button>

          <ProcessLoader isOpen={inProgress} />
        </div>
      </Form>

      <FlashAlert isOpen={alert.show} toggle={toggleAlert} type={alert.type} wrapperClass="mt-3" />
    </React.Fragment>
  );
};

AddToCollectionModal.propTypes = {
  document: PropTypes.object.isRequired,
  _helpers: PropTypes.object.isRequired,
};

export default AddToCollectionModal;
